/*
 * ototo-ds-hero.js — which hero slide is showing, and the clock that advances it.
 *
 * Slides are all server-rendered and one is revealed; the script never builds
 * markup and never holds a price. Without it the first slide shows, its button
 * is a real link, and the page simply stops switching.
 *
 * ⚠️ `hidden` is only a default — any `display` rule out-specifies it. The CSS
 * declares [hidden]{display:none !important} against .ot-hero.
 *
 * ⚠️ THE ROTATION STOPS DEAD UNDER prefers-reduced-motion rather than going
 * slower. Storefront.dc.html makes the same call and gives the reason: this is
 * a content carousel, not decoration, and a headline that swaps itself out from
 * under someone reading it is worse than one that never moves.
 */
(function () {
  'use strict';
  var root = document.querySelector('[data-ot-hero]');
  if (!root) return;
  var slides = [].slice.call(root.querySelectorAll('[data-ot-hero-slide]'));
  var dots = [].slice.call(root.querySelectorAll('[data-ot-hero-dot]'));
  if (slides.length < 2) return;

  var current = 0;

  function show(i) {
    current = i;
    slides.forEach(function (sl, n) {
      if (n === i) sl.removeAttribute('hidden');
      else sl.setAttribute('hidden', '');
    });
    dots.forEach(function (d) {
      d.setAttribute('aria-selected', String(parseInt(d.getAttribute('data-ot-hero-dot'), 10) === i));
    });
  }

  /* ── the clock ───────────────────────────────────────────────────────────
     Touching a chip RESTARTS it, so a shopper who just picked slide 3 gets a
     full turn on it instead of losing it a second later. Same reason the band
     pauses on hover and while the tab is in the background: an advance nobody
     is there to see is an advance that only costs the reader their place. */
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var autoplay = root.getAttribute('data-ot-hero-autoplay') !== 'false';
  var delay = (parseFloat(root.getAttribute('data-ot-hero-delay')) || 7) * 1000;
  var timer = null;

  /* ── the progress rail ───────────────────────────────────────────────────
     A CSS animation rather than a rAF loop writing a custom property every
     frame: `scaleX` runs on the compositor, and the browser already owns a
     clock that matches the one below. The script's only job is to (re)start it
     on the right chip.

     ⚠️ THE ANIMATION MUST BE CLEARED AND THE ELEMENT REFLOWED BEFORE IT IS
     REASSIGNED. Setting the same `animation` string on an element already
     running it is a no-op — the rail would fill once, on the first slide, and
     then sit full for the rest of the rotation. Reading `offsetWidth` between
     the two writes is what forces the style to be recomputed.

     ⚠️ CLEARED ON EVERY CHIP, not just the one being left. `show()` can be
     reached from a click on any chip and from the interval, so tracking which
     rail was last running is one more piece of state to get wrong.

     ⚠️⚠️ `dots[i]` IS NOT THE CHIP FOR SLIDE i. The tablist is rendered INSIDE
     each slide, so a three-slide hero has NINE dot buttons — three copies of
     every chip, one per slide, and only the visible slide's copy is on screen.
     `dots[2]` is slide 0's third chip, which is hidden the moment slide 2 is
     the one showing. Indexing the array put the animation on an invisible
     element and the rail appeared to work only while slide 0 was up. Match on
     `data-ot-hero-dot` and animate every copy — they are identical, only one is
     ever visible, and `show()` already sets `aria-selected` the same way. */
  function railsFor(i) {
    return dots
      .filter(function (d) { return parseInt(d.getAttribute('data-ot-hero-dot'), 10) === i; })
      .map(function (d) { return d.querySelector('[data-ot-hero-progress]'); })
      .filter(Boolean);
  }

  function runRail() {
    var all = [];
    dots.forEach(function (d) {
      var r = d.querySelector('[data-ot-hero-progress]');
      if (r) { r.style.animation = 'none'; all.push(r); }
    });
    var rails = railsFor(current);
    if (!rails.length || !autoplay || reduce) return;
    /* One read flushes pending style and layout for the whole document, so it
       covers every rail cleared above — not just this one. */
    void all[0].offsetWidth;
    rails.forEach(function (r) {
      r.style.animation = 'ot-hero-fill ' + (delay / 1000) + 's linear forwards';
    });
  }

  function stop() {
    if (timer) { window.clearInterval(timer); timer = null; }
    /* ⚠️ PAUSED, NOT CLEARED. Hovering the hero holds the rail where it is;
       clearing it would blank the chip and read as the slide being cancelled.
       On resume `start()` restarts both the clock and the rail from zero —
       which is honest, because `start()` really does give a full fresh turn. */
    railsFor(current).forEach(function (r) { r.style.animationPlayState = 'paused'; });
  }

  /* WCAG 2.2.2: the ONE site-wide motion control (the announcement bar's pause
     button) fires `ot:motion` on document. While paused, hover, focus and tab
     visibility no longer restart the clock; only pressing play again does. */
  var paused = document.documentElement.classList.contains('ot-motion-paused');
  document.addEventListener('ot:motion', function (e) {
    paused = !!(e.detail && e.detail.paused);
    if (paused) stop(); else start();
  });

  function start() {
    stop();
    if (!autoplay || reduce || paused) return;
    timer = window.setInterval(function () {
      show((current + 1) % slides.length);
      runRail();
    }, delay);
    /* The shorthand in runRail() resets `animation-play-state` to running, so
       nothing has to undo what stop() set. */
    runRail();
  }

  root.addEventListener('click', function (e) {
    var dot = e.target.closest && e.target.closest('[data-ot-hero-dot]');
    if (!dot) return;
    show(parseInt(dot.getAttribute('data-ot-hero-dot'), 10));
    start();
  });

  root.addEventListener('mouseenter', stop);
  root.addEventListener('mouseleave', start);
  root.addEventListener('focusin', stop);
  root.addEventListener('focusout', start);
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) stop(); else start();
  });

  start();
})();
